// src/components/Minihome/widgets/Guestbook.jsx

import { useEffect, useState, useCallback } from "react";
import { useLocation } from 'react-router-dom';
import { Card, CardContent, Typography, TextField, Button, Stack, IconButton, Tooltip, Box } from "@mui/material";
// ✅ 1. 필요한 API와 아이콘들을 모두 임포트합니다.
import {
    getGuestbook,
    postGuestbook,
    deleteGuestbook,
    updateGuestbookEntry // 방금 추가한 API
} from "../../../api/minihome";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';     // 답글 수정 아이콘
import CloseIcon from '@mui/icons-material/Close'; // 답글 삭제 아이콘
import ReplyIcon from '@mui/icons-material/Reply'; // ✅ 이 줄을 추가하세요.
import dayjs from 'dayjs';

export default function Guestbook({ home, canModerate, username, item }) {
    const backgroundColor = item?.backgroundColor || 'rgba(255,255,255,0.8)';

    const [items, setItems] = useState([]);
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);

    // ✅ 2. 답글 기능에 필요한 State들을 추가합니다.
    const [replyingId, setReplyingId] = useState(null); // 현재 답글/수정 중인 방명록 ID
    const [replyContent, setReplyContent] = useState(""); // 답글 내용
    const [replyLoading, setReplyLoading] = useState(false); // 답글 전송 로딩

    const load = useCallback(async () => {
        if (!username) return;
        setLoading(true);
        try {
            const { data } = await getGuestbook(username);
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        load();
    }, [load]);

    // --- 👇 3-1. 이 useEffect 전체를 추가하세요 ---
    const location = useLocation(); // URL 정보 가져오기

    useEffect(() => {
        // 방명록 아이템들이 로드되었고, URL 해시값이 '#guestbook-'으로 시작하는지 확인
        if (items.length > 0 && location.hash && location.hash.startsWith('#guestbook-')) {
            // 해시값에서 ID 부분만 추출 (예: '#guestbook-123' -> 'guestbook-123')
            const elementId = location.hash.substring(1);
            // 해당 ID를 가진 요소를 찾습니다.
            const element = document.getElementById(elementId);

            if (element) {
                // 요소를 찾았다면, 부드럽게 스크롤합니다.
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // (선택사항) 스크롤 후 강조 효과 추가 (예: 배경색 잠시 변경)
                element.style.backgroundColor = 'rgba(255, 255, 0, 0.2)'; // 노란색 배경
                setTimeout(() => {
                    element.style.backgroundColor = ''; // 1.5초 후 배경색 제거
                }, 1500);
            }
        }
        // 이 효과는 아이템 목록(items)이나 URL 해시(location.hash)가 변경될 때마다 실행됩니다.
    }, [items, location.hash]);

    const onSubmit = async () => {
        if (!content.trim() || !username) return;
        setLoading(true); // ✅ 로딩 시작
        await postGuestbook(username, { content, is_anonymous: false });
        setContent("");
        await load(); // ✅ load()가 끝날 때까지 기다립니다.
        setLoading(false); // ✅ 로딩 완료
    };

    const onDelete = async (id) => {
        if (!username) return;
        if (window.confirm("이 방명록 글을 정말로 삭제하시겠습니까? 복구할 수 없습니다.")) {
            try {
                await deleteGuestbook(username, id);
                load();
            } catch (err) {
                console.error("방명록 삭제 실패:", err);
                alert("삭제에 실패했습니다.");
            }
        }
    };

    // --- ✅ 3. 답글 관련 핸들러 함수들 추가 ---

    // "답글 달기" 또는 "수정" 버튼 클릭 시
    const handleOpenReplyEditor = (g) => {
        setReplyingId(g.id);
        setReplyContent(g.reply || ""); // 기존 답글이 있으면 내용 채우기
    };

    // "취소" 버튼 클릭 시
    const handleCancelReply = () => {
        setReplyingId(null);
        setReplyContent("");
    };

    // "저장" (답글 제출) 버튼 클릭 시
    const handleSubmitReply = async (id) => {
        if (!replyContent.trim()) {
            alert("답글 내용을 입력해주세요.");
            return;
        }
        setReplyLoading(true);
        try {
            // updateGuestbookEntry API 호출 (urls.py의 /reply/ 경로)
            await updateGuestbookEntry(username, id, { reply: replyContent });
            handleCancelReply(); // 입력창 닫기
            load(); // 목록 새로고침
        } catch (err) {
            console.error("답글 작성 실패:", err);
            alert("답글 작성에 실패했습니다.");
        } finally {
            setReplyLoading(false);
        }
    };

    // "답글 삭제" (X) 버튼 클릭 시
    const handleDeleteReply = async (id) => {
        if (window.confirm("이 답글을 삭제하시겠습니까?")) {
            setReplyLoading(true);
            try {
                // updateGuestbookEntry API에 reply: null 을 보내서 삭제
                await updateGuestbookEntry(username, id, { reply: null });
                handleCancelReply();
                load();
            } catch (err) {
                console.error("답글 삭제 실패:", err);
                alert("답글 삭제에 실패했습니다.");
            } finally {
                setReplyLoading(false);
            }
        }
    };
    // --- ✅ 답글 핸들러 끝 ---


    return (
        <Card elevation={canModerate ? 2 : 0} sx={{
            height: "100%", display: "flex", flexDirection: "column", backgroundColor: backgroundColor
        }}>
            <CardContent sx={{ flex: 1, overflow: "auto", p: 0 }}>
                <Box sx={{ px: 2, pt: 1, pb: 0.75 }}>
                    <Typography variant="h6">방명록</Typography>
                </Box>

                {/* --- 1. 기존 방명록 작성 폼 (sticky) --- */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0, zIndex: (theme) => theme.zIndex.appBar, // 겹침 방지
                        bgcolor: "background.paper",            // 불투명 배경 (겹침 느낌 해소)
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        boxShadow: (theme) => theme.shadows[1], // 살짝 분리감
                        px: 2,
                        py: 1,
                    }}
                >
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            fullWidth
                            sx={{ flexGrow: 1, flexShrink: 1, flexBasis: 'auto' }} // ✅ 입력창만 확장
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="한 줄 남기기"
                            onMouseDown={(e) => e.stopPropagation()}
                            className="no-drag"
                            disabled={loading || !!replyingId}
                        />
                        <Button
                            variant="contained"
                            onClick={onSubmit}
                            disabled={loading || !content.trim() || !!replyingId}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="no-drag"
                            sx={{
                                flexGrow: 0,     // ✅ 버튼은 확장 금지
                                flexShrink: 0,
                                width: 'auto',   // ✅ 내용에 맞게 크기
                                minWidth: 'fit-content',
                                px: 2,
                            }}
                        >
                            {loading ? "작성 중…" : "작성"}
                        </Button>
                    </Stack>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        {items.map((g) => (
                            // ✅ 4. 각 항목을 Box로 감싸서 답글 UI 공간 확보
                            <Box key={g.id}
                                id={`guestbook-${g.id}`}
                                sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 1 }}>
                                <Stack
                                    spacing={0.5}
                                    sx={{ alignItems: 'flex-start' }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{
                                            width: '100%',
                                            position: 'relative',
                                            zIndex: 10
                                        }}
                                    >
                                        {/* --- ✅ 왼쪽 영역 (작성자 + 숨김 태그) --- */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                <Box component="span" fontWeight="bold" color="text.primary">
                                                    {g.author_username ?? "익명"}
                                                </Box>
                                                {` (${dayjs(g.created_at).format('YYYY년 MM월 DD일 HH:mm')})`}
                                            </Typography>
                                            {g.is_hidden && canModerate && (
                                                <Box sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem', flexShrink: 0 }}>(숨김)</Box>
                                            )}
                                        </Box>

                                        {/* --- ✅ 오른쪽 영역 (아이콘 그룹) --- */}
                                        {canModerate && !replyingId && (
                                            <Stack direction="row" spacing={0} sx={{ ml: 'auto', flexShrink: 0 }}>

                                                {/* "답글 달기" 아이콘 (답글이 없을 때만 표시) */}
                                                {!g.reply && (
                                                    <Tooltip title="답글 달기">
                                                        <IconButton
                                                            onClick={() => handleOpenReplyEditor(g)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            className="no-drag"
                                                            sx={{ width: 32, height: 32, p: 0 }}
                                                        >
                                                            <ReplyIcon sx={{ fontSize: '1.25rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {/* "글 삭제" 아이콘 */}
                                                <Tooltip title="글 삭제">
                                                    <IconButton
                                                        onClick={() => onDelete(g.id)}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        className="no-drag"
                                                        sx={{ width: 32, height: 32, p: 0 }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: '1.25rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                    </Stack>

                                    <Typography variant="body2" sx={{ wordBreak: 'break-all', textAlign: 'left', ml: '4px', mt: '4px' }}>
                                        {g.content}
                                    </Typography>
                                </Stack>

                                {/* // ✅ 5. 답글 UI 렌더링 로직
                            */}
                                {replyingId === g.id ? (
                                    // --- 5-1. "수정" 또는 "답글 달기" 클릭 시: 입력창 표시 ---
                                    <Box sx={{ mt: 1, ml: 3, pl: 1.5, borderLeft: '3px solid #ddd' }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            size="small"
                                            variant="outlined"
                                            placeholder="답글 입력..."
                                            value={replyContent}
                                            onChange={(e) => setReplyContent(e.target.value)}
                                            disabled={replyLoading}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="no-drag"
                                        />
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                                            <Button size="small" onClick={handleCancelReply} disabled={replyLoading} className="no-drag" onMouseDown={(e) => e.stopPropagation()}>
                                                취소
                                            </Button>
                                            <Button size="small" variant="contained" onClick={() => handleSubmitReply(g.id)} disabled={replyLoading} className="no-drag" onMouseDown={(e) => e.stopPropagation()}>
                                                {replyLoading ? "저장 중..." : "저장"}
                                            </Button>
                                        </Stack>
                                    </Box>
                                ) : g.reply ? (
                                    // --- 5-2. 답글이 존재할 때: 답글 내용 표시 ---
                                    <Box
                                        sx={{
                                            mt: 1, ml: 3, pl: 1.5,
                                            borderLeft: '3px solid #ddd',
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <Stack spacing={0.5} sx={{ p: 1 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="caption" color="text.secondary">
                                                    <Box component="span" fontWeight="bold" color="primary.main">
                                                        {home?.nickname || home?.username || "주인장"}
                                                    </Box>
                                                    {g.replied_at && ` (${dayjs(g.replied_at).format('YYYY.MM.DD HH:mm')})`}
                                                </Typography>
                                                {/* 주인장일 때만 수정/삭제 아이콘 표시 */}
                                                {canModerate && (
                                                    <Stack direction="row" spacing={0}>
                                                        <Tooltip title="답글 수정">
                                                            <IconButton size="small" onClick={() => handleOpenReplyEditor(g)} className="no-drag" onMouseDown={(e) => e.stopPropagation()} sx={{ width: 28, height: 28 }}>
                                                                <EditIcon sx={{ fontSize: '1rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="답글 삭제">
                                                            <IconButton size="small" onClick={() => handleDeleteReply(g.id)} className="no-drag" onMouseDown={(e) => e.stopPropagation()} sx={{ width: 28, height: 28 }}>
                                                                <CloseIcon sx={{ fontSize: '1.1rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                )}
                                            </Stack>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all', textAlign: 'left' }}>
                                                {g.reply}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                ) : null}
                            </Box>
                        ))}

                        {/* 방명록 로딩/empty 표시 */}
                        {!items.length && !loading && <Typography variant="body2" color="text.secondary">아직 방명록이 없어요.</Typography>}
                        {loading && items.length === 0 && <Typography variant="caption" color="text.secondary">불러오는 중…</Typography>}
                    </Stack>
                </Box>


            </CardContent>
        </Card>
    );
}